import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MessageService } from '@corelabs/angular/messages';
import { TranslateService } from '@ngx-translate/core';
import { catchError, concatMap, EMPTY, exhaustMap, map, of, switchMap } from 'rxjs';
import { WarehouseError } from '@app/store/shared/models';
import { palletsActions } from '../actions/pallets.actions';
import { HttpService } from '../services/http.service';
import { finalizeLoader } from '@app/shared/components';

@Injectable()
export class PalletsEffects {
    private readonly actions$ = inject(Actions);
    private readonly httpService = inject(HttpService);
    private readonly messageService = inject(MessageService);
    private readonly translateService = inject(TranslateService);

    loadPallets$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(palletsActions.loadPallets),
            switchMap(({ status, page }) => {
                return this.httpService.loadPallets(status, page).pipe(
                    finalizeLoader('pallets-load'),
                    map(({ pallets, hasMore }) => palletsActions.loadPalletsSuccess({ payload: pallets, page, hasMore })),
                    catchError((error: HttpErrorResponse) => of(palletsActions.loadPalletsError({ code: error.status }))),
                );
            }),
        );
    });

    generatePallets$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(palletsActions.generatePallets),
            exhaustMap(({ quantity }) => {
                return this.httpService.generatePallets(quantity).pipe(
                    finalizeLoader('pallets-generate'),
                    map(() => palletsActions.generatePalletsSuccess()),
                    catchError((error: WarehouseError) => {
                        if (error.reason === 'duplicate_request') {
                            return of(palletsActions.generatePalletsSuccess());
                        }
                        this.messageService.add(this.translateService.instant(error.key), 'danger');
                        return of(palletsActions.generatePalletsError({ code: error.code, key: error.key }));
                    }),
                );
            }),
        );
    });

    printPallet$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(palletsActions.printPallet),
                concatMap(({ code }) => {
                    return this.httpService.printPallet(code).pipe(
                        catchError((error: WarehouseError) => {
                            this.messageService.add(this.translateService.instant(error.key), 'danger');
                            return EMPTY;
                        }),
                    );
                }),
            );
        },
        { dispatch: false },
    );

    loadPallet$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(palletsActions.loadPallet),
            switchMap(({ code }) => {
                return this.httpService.loadPallet(code).pipe(
                    finalizeLoader('pallet-load'),
                    map((payload) => palletsActions.loadPalletSuccess({ payload })),
                    catchError((error: HttpErrorResponse) => of(palletsActions.loadPalletError({ code: error.status }))),
                );
            }),
        );
    });

    addCarton$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(palletsActions.addCarton),
            concatMap(({ code, cartonCode }) => {
                return this.httpService.addCarton(code, cartonCode).pipe(
                    finalizeLoader('pallet-add-carton'),
                    map((payload) => palletsActions.addCartonSuccess({ payload })),
                    catchError((error: WarehouseError) => {
                        this.messageService.add(this.translateService.instant(error.key), 'danger');
                        return of(palletsActions.addCartonError({ code: error.code, key: error.key }));
                    }),
                );
            }),
        );
    });

    removeCarton$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(palletsActions.removeCarton),
            concatMap(({ code, cartonCode }) => {
                return this.httpService.removeCarton(code, cartonCode).pipe(
                    finalizeLoader('pallet-remove-carton'),
                    map((payload) => palletsActions.removeCartonSuccess({ payload })),
                    catchError((error: WarehouseError) => {
                        this.messageService.add(this.translateService.instant(error.key), 'danger');
                        return of(palletsActions.removeCartonError({ code: error.code, key: error.key }));
                    }),
                );
            }),
        );
    });

    closePallet$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(palletsActions.closePallet),
            exhaustMap(({ code }) => {
                return this.httpService.closePallet(code).pipe(
                    finalizeLoader('pallet-close'),
                    map((payload) => palletsActions.closePalletSuccess({ payload })),
                    catchError((error: WarehouseError) => {
                        if (error.reason === 'duplicate_request') {
                            this.messageService.add(this.translateService.instant(error.key), 'warning');
                            return of(palletsActions.closePalletError({ code: error.code, key: error.key }), palletsActions.loadPallet({ code }));
                        }
                        this.messageService.add(this.translateService.instant(error.key), 'danger');
                        return of(palletsActions.closePalletError({ code: error.code, key: error.key }));
                    }),
                );
            }),
        );
    });
}
